import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/client'
import { useShelfScan } from '@/features/shelf/useShelfScan'

import { ScanResultSheet } from './ScanResultSheet'
import { resolveCode, type ScanResult } from './api'
import { useKeyboardWedge } from './useKeyboardWedge'

/**
 * Resolve a scanned code and act on it.
 *
 * Anything that is simply a place in the app navigates there; a lot or a
 * distributor label opens a sheet, because both are the start of a stock
 * movement rather than a destination.
 */
export function useScanAction() {
  const navigate = useNavigate()
  const [result, setResult] = useState<ScanResult | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handle(code: string) {
    setPending(true)
    setError(null)
    try {
      const found = await resolveCode(code)
      if (found.kind === 'part' && found.id) {
        setResult(null)
        await navigate({ to: '/parts/$partId', params: { partId: found.id } })
        return
      }
      if (found.kind === 'storage_location' && found.id) {
        setResult(null)
        await navigate({ to: '/locations/$locationId', params: { locationId: found.id } })
        return
      }
      setResult(found)
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.problem.detail : 'That code could not be looked up.',
      )
    } finally {
      setPending(false)
    }
  }

  return { handle, result, clear: () => setResult(null), pending, error }
}

/**
 * Mounted once, app-wide: a wedge scanner works from whatever page is open. On
 * the shelf a scan opens the drawer it names (D38); at the desk it goes to the
 * part or place, or opens the lot sheet.
 */
export function GlobalScanListener() {
  const shelf = useRouterState({
    select: (state) => state.location.pathname.startsWith('/shelf'),
  })
  const desk = useScanAction()
  const onShelf = useShelfScan()
  useKeyboardWedge((code) => void (shelf ? onShelf.handle(code) : desk.handle(code)))

  // A wedge scan happens on any page; a failure must not depend on the page
  // having a place to show it. One effect per source: a stale error from the
  // other side must not re-toast when the shelf flag flips.
  useEffect(() => {
    if (onShelf.error) toast.error(onShelf.error)
  }, [onShelf.error])
  useEffect(() => {
    if (desk.error) toast.error(desk.error)
  }, [desk.error])

  if (shelf) {
    return onShelf.sheet ? (
      <ScanResultSheet result={onShelf.sheet} onClose={onShelf.closeSheet} />
    ) : null
  }
  return desk.result ? <ScanResultSheet result={desk.result} onClose={desk.clear} /> : null
}
